import { useState, useMemo, useCallback} from 'react';
import { createActionHandlers } from './actionHandlers.js';
import { createCellRenderer } from './cellRenderers.js';
import api from "../../../services/api.js"
import config from "../../../config.js"
import { Toast } from "../../../shared/Toast.js"

export const useTableState = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [adsData, setAdsData] = useState([]);
    const [designers, setDesigners] = useState([]);
    const [contentWriters, setContentWriters] = useState([])
    const [filters, setFilters] = useState({});
    
    const [adCounts, setAdCounts] = useState({
        total: 0,
        pending_ads: 0,
        ready_to_live: 0,
        completed_ads: 0,
        myads: 0,
        unassigned: 0,
        unpicked_ready_to_live: 0,
        unpicked: 0
    })

    const [showActionsMenu, setShowActionsMenu] = useState(-1);
    const [modal, setModal] = useState({ open: false, type: null, image: null, timelineData: null });
    const [drawer, setDrawer] = useState({ open: false, type: null, rowData: null });
    const [expandedVersionsRow, setExpandedVersionsRow] = useState(null);
    const [goLiveLoadingIdx, setGoLiveLoadingIdx] = useState(null);

    const actionHandlers = useMemo(() => createActionHandlers({
        setModal,
        setDrawer,
        setExpandedVersionsRow,
        setShowActionsMenu
    }), []);

    const renderCell = useMemo(() => createCellRenderer(), []);

    // Generic data fetching functions

    const fetchAdCounts = async () => {
        const res = await api({
            url: `/alpha_cst_designer/getAdCount`,
            method: "POST",
            responseType: "json",
            data: {}
        });
        if (res.code === 200) {
            setAdCounts(res.data.data);
        }
    };
   

    const fetchAds = useCallback(async (endpoint, page = currentPage, isActive = true, filtersArg = {}) => {
        if (!isActive) return;
        setLoading(true);
        try {
            const res = await api({
                url: endpoint,
                method: "POST",
                responseType: "json",
                data: {
                    page: page,
                    limit: config.PER_PAGE,
                    ...filtersArg,
                }
            });
            if (res.code === 200) {
                setAdsData(res.data?.data || []);
                setTotalPages(Math.ceil(res.data?.total / config.PER_PAGE) || 0);
            }
        } catch (error) {
            console.error(`Error fetching ads from ${endpoint}:`, error);
            setAdsData([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    // Designer related functions
    const fetchDesigners = useCallback(async () => {
        try {
            const res = await api({
                url: `/alpha_cst_designer/getDesigners`,
                method: "POST",
                responseType: "json",
                data: {}
            });
            if (res.code === 200) {
                setDesigners(res.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching designers:', error);
            setDesigners([]);
        }
    }, []);
    const fetchContentWriters = useCallback(async () => {
        try {
            const res = await api({
                url: `/user/content_writer/contentWriters`,
                method: "POST",
                responseType: "json",
                data: {}
            });
            if (res.code === 200) {
                setContentWriters(res.data || []);
            }
        } catch (error) {
            console.error('Error fetching designers:', error);
            setContentWriters([]);
        }
    }, []);
    

    // Specific fetch functions for different ad types
    const fetchPendingAds = useCallback((page = currentPage, isActive = true, filtersArg = {}) => {
        return fetchAds("/alpha_cst_designer/my-ads-pending", page, isActive, filtersArg);
    }, [fetchAds, currentPage]);

    const fetchReadyToLiveAds = useCallback((page = currentPage, isActive = true, filtersArg = {}) => {
        return fetchAds("/alpha_cst_designer/my-ads-ready_to_live", page, isActive, filtersArg);
    }, [fetchAds, currentPage]);

    const fetchCompletedAds = useCallback((page = currentPage, isActive = true, filtersArg = {}) => {
        return fetchAds("/alpha_cst_designer/my-ads-completed", page, isActive, filtersArg);
    }, [fetchAds, currentPage]);

    const fetchAllAds = useCallback((page = currentPage, isActive = true, filtersArg = {}) => {
        return fetchAds("/alpha_cst_designer/all-ads", page, isActive, filtersArg);
    }, [fetchAds, currentPage]);

    
    const handleDesignerChange = useCallback(async (creativeId, designerId,designerName) => {
        if (!designerId) {
            Toast('warning', 'Please select a designer.');
            return;
        }
        setLoading(true);
        try {
            const res = await api({
                url: '/alpha_cst_designer/changeDesigner',
                method: 'POST',
                responseType: 'json',
                data: {
                    creative_id: creativeId,
                    designer_id: parseInt(designerId),
                    designer_name:designerName
                },
            });
            if (res.code === 200) {
                Toast('success', 'Designer assigned successfully');
                fetchPendingAds();
            } else {
                Toast('error', res.data?.message || 'Failed to assign designer');
            }
        } catch (error) {
            Toast('error', 'Error assigning designer');
        } finally {
            setLoading(false);
        }
    }, [designers, fetchPendingAds, currentPage]);

    const handleContentWriterChange = useCallback(async (content_id,creativeId, contentWriterId, contentWriterName, description) => {
        if (!contentWriterId) {
            Toast('warning', 'Please select a content writer.');
            return;
        }
        setLoading(true);
        try {
            const res = await api({
                url: '/alpha_cst_designer/contentWriter',
                method: 'POST',
                responseType: 'json',
                data: {
                    content_id: content_id || null,
                    creative_id: creativeId,
                    content_writer_id: parseInt(contentWriterId) || null,
                    content_writer_name: contentWriterName || null,
                    description: description
                },
            });
            if (res.code === 200) {
                Toast('success', 'Content writer assigned successfully');
                fetchPendingAds();
            } else {
                Toast('error', res.data?.message || 'Failed to assign content writer');
            }
        } catch (error) {
            Toast('error', 'Error assigning content writer');
        } finally {
            setLoading(false);
        }
    }, [contentWriters, fetchPendingAds, currentPage]);
    const fetchContent = async(creative_id) => {
        if(!creative_id) return [];
        const result = await api({
            url:`/user/content_writer/getContent`,
            method: "POST",
            responseType:"json",
            data:{creative_id}
        })
        return result.data || [];
    }
    const handleGoToLive = useCallback(async (creative,creativeId, rowIdx) => {
        setGoLiveLoadingIdx(rowIdx);
        try {
            const res = await api({
                url: '/alpha_cst_designer/goLive',
                method: 'POST',
                responseType: 'json',
                data: { creative_id: creativeId }
            });
            if (res.code === 200) {
                Toast('success', 'Ad went live successfully');
                fetchReadyToLiveAds();
            } else {
                Toast('error', res.data?.message || 'Failed to go live');
            }
        } catch (error) {
            Toast('error', 'Error going live');
        } finally {
            setGoLiveLoadingIdx(null);
            setLoading(false);
        }
    }, [fetchReadyToLiveAds]);


    // Page change handlers
    const handlePageChange = (event)=>{
        const newPage = event.selected;
        setCurrentPage(newPage);
    }
    const handlePageClick = (event) => {
        const newPage = event.selected;
        setCurrentPage(newPage);
        setExpandedVersionsRow(null);
        setShowActionsMenu(-1);
    }

    // UI utility functions
    const getModalAlt = useCallback((type) => {
        return type === 'latestDesign' ? 'Latest Design' : 'Timeline';
    }, []);

    const closeExpandedRows = useCallback(() => {
        setExpandedVersionsRow(null);
        setShowActionsMenu(-1);
    }, []);


    return {
        // State
        currentPage,
        loading,
        totalPages,
        adsData,
        designers,
        adCounts,
        showActionsMenu,
        modal,
        drawer,
        expandedVersionsRow,
        filters,
        goLiveLoadingIdx,
        contentWriters,

        // Data fetching functions
        fetchAds,
        fetchPendingAds,
        fetchReadyToLiveAds,
        fetchCompletedAds,
        fetchAllAds,
        fetchDesigners,
        fetchAdCounts,
        fetchContentWriters,
        fetchContent,

        // Handlers
        actionHandlers,
        renderCell,
        handlePageClick,
        handlePageChange,
        getModalAlt,
        closeExpandedRows,
        handleDesignerChange,
        handleContentWriterChange,
        handleGoToLive,
        // Setters
        setCurrentPage,
        setAdCounts,
        setShowActionsMenu,
        setModal,
        setDrawer,
        setExpandedVersionsRow,
        setAdsData,
        setTotalPages,
        setLoading,
        setDesigners,
        setFilters,
        setContentWriters,
    };
}; 